import React, { useEffect, useState } from 'react';
import { Button, Progress, Typography } from 'antd';
import { CheckCircleOutlined, CloseOutlined, DownOutlined, RedoOutlined, UpOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';

import PartStore from '../../stores/PartStore';

const { Text } = Typography;

/** Staff background upload progress for parts added from the match modal. */
export const PartUploadTray = observer(() => {
    const [expanded, setExpanded] = useState(false);
    const jobs = PartStore.uploadJobs;
    const active = jobs.some(job => job.status === 'queued' || job.status === 'uploading');

    useEffect(() => {
        if (!active) return;

        // Uploads are not resumed after a reload, so warn before leaving the page.
        const warnBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnBeforeUnload);
        return () => window.removeEventListener('beforeunload', warnBeforeUnload);
    }, [active]);

    if (!jobs.length || PartStore.uploadTrayDismissed) return null;

    const completed = jobs.filter(job => job.status === 'completed').length;
    const failed = jobs.filter(job => job.status === 'failed').length;
    const aggregateProgress = Math.round(jobs.reduce((total, job) => total + job.progress, 0) / jobs.length);
    const current = jobs.find(job => job.status === 'uploading');

    return (
        <div
            data-active={active ? 'true' : 'false'}
            data-completed={completed}
            data-failed={failed}
            data-testid="otf-part-upload-tray"
            data-total={jobs.length}
            className="part-upload-tray"
            style={{
                position: 'fixed',
                right: 24,
                // Sit above the staff portal's floating chat button (bottom-right, 56px).
                bottom: 96,
                zIndex: 1100,
                width: expanded ? 360 : 300,
                // Keep a 24px gutter on both sides so the tray never overflows narrow viewports.
                maxWidth: 'calc(100vw - 48px)',
                borderRadius: 10,
                background: '#fff',
                border: '1px solid #d9d9d9',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
                overflow: 'hidden',
            }}
        >
            <div
                className="part-upload-tray-header"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    background: '#fafafa',
                    borderBottom: expanded ? '1px solid #eee' : 0,
                }}
            >
                <button
                    type="button"
                    onClick={() => setExpanded(value => !value)}
                    style={{
                        border: 0,
                        background: 'transparent',
                        padding: 0,
                        textAlign: 'left',
                        flex: 1,
                        minWidth: 0,
                        cursor: 'pointer',
                    }}
                >
                    <Text strong>
                        {active
                            ? `Adding parts ${completed} of ${jobs.length}`
                            : `Added ${completed} of ${jobs.length}`}
                    </Text>
                    {failed ? <Text type="danger"> · {failed} failed</Text> : null}
                    {current ? (
                        <Text type="secondary" ellipsis style={{ display: 'block', maxWidth: '100%', fontSize: 12 }}>
                            {current.label}
                        </Text>
                    ) : null}
                </button>
                <Button
                    type="text"
                    size="small"
                    aria-label={expanded ? 'Minimize uploads' : 'Expand uploads'}
                    data-testid="otf-part-upload-toggle"
                    icon={expanded ? <DownOutlined /> : <UpOutlined />}
                    onClick={() => setExpanded(value => !value)}
                />
                {!active ? (
                    <Button
                        type="text"
                        size="small"
                        aria-label="Dismiss uploads"
                        data-testid="otf-part-upload-dismiss"
                        icon={<CloseOutlined />}
                        onClick={() => PartStore.dismissUploads()}
                    />
                ) : null}
            </div>

            <div style={{ padding: '0 12px 10px' }}>
                <Progress
                    percent={aggregateProgress}
                    showInfo={false}
                    size="small"
                    status={failed ? 'exception' : 'active'}
                />
            </div>

            {expanded ? (
                <div style={{ maxHeight: 220, overflowY: 'auto', padding: '0 12px 10px' }}>
                    {jobs.map(job => (
                        <div
                            key={job.uid}
                            className="part-upload-tray-row"
                            data-file-name={job.label}
                            data-testid="otf-part-upload-job"
                            data-status={job.status}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                minHeight: 34,
                                gap: 8,
                                borderTop: '1px solid #f0f0f0',
                            }}
                        >
                            <Text ellipsis style={{ flex: 1, minWidth: 0, fontSize: 12 }} title={job.label}>
                                {job.label}
                            </Text>
                            {job.status === 'completed' ? (
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            ) : (
                                <Text type={job.status === 'failed' ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                                    {job.status === 'uploading' ? `${job.progress}%` : job.status}
                                </Text>
                            )}
                        </div>
                    ))}
                    {failed ? (
                        <Button
                            block
                            data-testid="otf-part-upload-retry"
                            size="small"
                            icon={<RedoOutlined />}
                            onClick={() => PartStore.retryFailedUploads()}
                            style={{ marginTop: 8 }}
                        >
                            Retry failed uploads
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
});

export default PartUploadTray;
